/**
 * chat.ts — room chat limits and the text sanitizer.
 *
 * Kept beside `armor.ts` rather than inside `protocol.ts` for the same reason:
 * the message names and payload shapes are the protocol's, but deciding what
 * counts as a legal message is a rule both ends need and the server enforces.
 *
 * The folding itself lives in `text.ts`, shared with display names.
 *
 * Chat is the one place a player types text that other players' browsers render,
 * so the sanitizer is the whole security surface of the feature. It is applied on
 * the SERVER — a client sanitizing its own outgoing text protects nobody, since
 * the next client along can send whatever it likes. The client renders through
 * `textContent`, never `innerHTML`, which is the other half of the same rule.
 */
import { toSingleLine } from './text.js';
/** Longest message a player may send. Longer is truncated, not refused. */
export const CHAT_MAX = 120;
/**
 * Shortest gap between one player's messages, ms. Flood control, enforced
 * server-side. Generous enough to type at speed, tight enough that a scripted
 * client cannot fill everyone's log faster than they can scroll it.
 */
export const CHAT_MIN_INTERVAL_MS = 500;
/** Reduces untrusted chat text to one short line, or undefined if nothing is left. */
export function sanitizeChatText(raw) {
    return toSingleLine(raw, CHAT_MAX);
}
/**
 * Whether a player who last spoke at `lastAtMs` may speak again at `nowMs`.
 *
 * A predicate over timestamps rather than a method on a timer, so the flood rule
 * can be tested without waiting in real time — and so the room only has to store
 * one number per player.
 *
 * `lastAtMs` of 0 means "has not spoken", which is always allowed: on a server
 * fresh enough that `Date.now()` is small this is still true, because the gap is
 * then enormous rather than negative.
 */
export function chatAllowedAt(lastAtMs, nowMs) {
    return nowMs - lastAtMs >= CHAT_MIN_INTERVAL_MS;
}
//# sourceMappingURL=chat.js.map